import { Form } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import {
    createFieldAttachment,
    createFieldCheckbox,
    createFieldInput,
    createFieldInputNumber,
    createFieldSelect,
    createFieldTemplateBuilder
} from "../../../../../components/input/template-builder/template-builder.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import { conditionFormChecker, putField } from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { filter } from "../../../../../utils/search";
import { callHazSubStanceUnit } from "./haz-info.section";

const CSelect = defineAsyncComponent(() => import("../../../../../components/input/template-builder/select/CSelect"));
const InputNumber = defineAsyncComponent(() => import("../../../../../components/input/template-builder/input-number/InputNumber"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/hazMainSubStance";

export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callSubStance = ({ searchQuery, refTypeKeyIn }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "refType.key", type: "String", operator: "in", value: refTypeKeyIn }
    ];
    if (searchQuery) {
        where.push({ name: "name", type: "String", operator: "like", value: searchQuery });
        where.push({ logic: "OR", name: "nameEn", type: "String", operator: "like", value: searchQuery });
        where.push({ logic: "OR", name: "refType.value", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/master/hazSubStance/search", {
            criteria: {
                select: ["**"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "name", type: "ASC" }]
            }
        })
        .then(page => page.data);
};
export const callUserCompany = ({ searchQuery }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "active" },
        { name: "isCeo", type: "Boolean", operator: "=", value: true }
    ];
    if (searchQuery) {
        where.push(
            { openBucket: true, name: "companyNameTh", type: "String", operator: "like", value: searchQuery },
            { logic: "OR", closeBucket: true, name: "fullNameTh", type: "String", operator: "like", value: searchQuery }
        );
    }
    return store
        .dispatch("resource/v1/security/user/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "version", "companyNameTh", "companyNameEn", "fullNameTh", "fullNameEn", "refPersonType.*"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "companyNameTh", type: "ASC" }],
                children: [
                    {
                        name: "hazes",
                        select: ["id", "version", "approveCode", "refUserCompany.id", "refUserCompany.version"],
                        page: 0,
                        size: 1000000,
                        where: [
                            { name: "isActive", type: "Boolean", operator: "=", value: true },
                            { name: "isTemp", type: "Boolean", operator: "=", value: false },
                            { name: "refStatus.key", type: "String", operator: "!=", value: "cancel" },
                            { name: "refType.key", type: "String", operator: "=", value: "1" }
                        ]
                    }
                ]
            }
        })
        .then(page => page.data);
};

export const createField = ({ props, data, key /* , isLoading */ }) => {
    const { viewOnly /* , menuContext */ } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}refSubStance`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazMainSubStance.refSubStance"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refSubStance,
                                        "onUpdate:value": value => {
                                            model.refSubStance = value;
                                            if (value?.refType?.key === "media") {
                                                model.chemiAnalysisResult = null;
                                                model.isBypassAnalysisResult = null;
                                                model.microAnalysisResult1 = null;
                                                model.microAnalysisResult2 = null;
                                                model.microAnalysisResult3 = null;
                                                model.qty = null;
                                                model.refUnit = null;
                                                model.microAnalysisQty1 = null;
                                                model.microAnalysisQty2 = null;
                                                model.microAnalysisQty3 = null;
                                                model.refMicroUnit = null;
                                                model.isImport = null;
                                                model.isInternal = null;
                                                model.docNoRefer = null;
                                                lodash.remove(model.companies, d => !d.id);
                                                lodash.remove(model.products, d => !d.id);
                                                model.companies.forEach(d => (d.isDelete = true));
                                                model.products.forEach(d => (d.isDelete = true));
                                                model.attachments[0].url = null;
                                                model.attachments[0].path = null;
                                                model.attachments[0].name = null;
                                                model.attachments[0].blob = null;
                                                model.attachments[0].size = null;
                                                model.attachments[0].type = null;
                                                model.attachments[0].documentName = null;
                                            }
                                        },
                                        disabled: viewOnly,
                                        list: ({ searchQuery }) => callSubStance({ searchQuery, refTypeKeyIn: [modelRef.refHazTemp.refSubStanceTypeMain?.key, "media"] }),
                                        optLabel: "nameEn",
                                        renderLabel: ({ item }) => <div>{item.nameEn || item.name} {item.refType?.value ? "(" + item.refType?.value + ")" : ""}</div>
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameEn`) }
                        }),
                        createFieldInputNumber({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}intense`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazMainSubStance.intense"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.intense,
                                        "onUpdate:value": value => (model.intense = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        addonAfter: () => <div>%</div>
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                )
                        })
                    ],
                    skeleton: isLoading.value
                })
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldInputNumber({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}chemiAnalysisResult`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazMainSubStance.chemiAnalysisResult"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.chemiAnalysisResult,
                                        "onUpdate:value": value => (model.chemiAnalysisResult = value),
                                        disabled: viewOnly,
                                        allowPrecision: true
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                )
                        }),
                        createFieldCheckbox({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}isBypassAnalysisResult`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            // label: () => i18n.global.t("model.hazMainSubStance.isBypassAnalysisResult"),
                            labelRender: () => " ",
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        checked: model?.isBypassAnalysisResult,
                                        "onUpdate:checked": checked => {
                                            model.isBypassAnalysisResult = checked;
                                        },
                                        disabled: viewOnly,
                                        text: () => i18n.global.t("model.hazMainSubStance.isBypassAnalysisResult")
                                    },
                                    conditionFormChecker({ props, data, executeKey: "checkboxField", params: { field } })
                                )
                        }),
                        createFieldInputNumber({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}qty`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazMainSubStance.qty"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.qty,
                                        "onUpdate:value": value => (model.qty = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        addonAfter: () => {
                                            const addonAfterProp = lodash.merge(
                                                {
                                                    value: model.refUnit,
                                                    "onUpdate:value": value => (model.refUnit = value),
                                                    disabled: viewOnly,
                                                    list: callHazSubStanceUnit,
                                                    optLabel: "value",
                                                    placeholder: i18n.global.t("button.unit"),
                                                    style: { width: "100px !important" }
                                                },
                                                conditionFormChecker({
                                                    props,
                                                    data,
                                                    executeKey: "inputPropField",
                                                    params: { field: { key: `${prefix}refUnit` }, model }
                                                })
                                            );
                                            return (
                                                <Form.Item {...useForm.validateInfos[`${prefix}refUnit`]} help={undefined}>
                                                    <CSelect {...addonAfterProp}></CSelect>
                                                </Form.Item>
                                            );
                                        }
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                )
                        }),
                        {
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}microAnalysisResult1`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({
                                labelCol: { xs: 24, md: 8 },
                                wrapperCol: { xs: 24, md: 16 },
                                validateStatus: undefined,
                                help: undefined
                            }),
                            label: () => i18n.global.t("views.request.hazInfo.microAnalysisResult1"),
                            inputRender: () => {
                                const inputPropField1 = lodash.merge(
                                    {
                                        value: model.microAnalysisResult1,
                                        "onUpdate:value": value => (model.microAnalysisResult1 = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}microAnalysisResult1` }, model }
                                    })
                                );
                                const inputPropField2 = lodash.merge(
                                    {
                                        value: model.microAnalysisResult2,
                                        "onUpdate:value": value => (model.microAnalysisResult2 = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}microAnalysisResult2` }, model }
                                    })
                                );
                                const inputPropField3 = lodash.merge(
                                    {
                                        value: model.microAnalysisResult3,
                                        "onUpdate:value": value => (model.microAnalysisResult3 = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}microAnalysisResult3` }, model }
                                    })
                                );
                                const inputPropField4 = lodash.merge(
                                    {
                                        value: model.refUnit,
                                        "onUpdate:value": value => (model.refUnit = value),
                                        disabled: viewOnly,
                                        list: callHazSubStanceUnit,
                                        optLabel: "value",
                                        placeholder: i18n.global.t("button.unit"),
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}refUnit` }, model }
                                    })
                                );
                                return (
                                    <>
                                        <Form.Item
                                            {...useForm.validateInfos[`${prefix}microAnalysisResult1`]}
                                            help={undefined}
                                            class="d-inline-block"
                                        >
                                            <InputNumber {...inputPropField1}></InputNumber>
                                        </Form.Item>
                                        <span> x </span>
                                        <Form.Item
                                            {...useForm.validateInfos[`${prefix}microAnalysisResult2`]}
                                            help={undefined}
                                            class="d-inline-block"
                                        >
                                            <InputNumber {...inputPropField2}></InputNumber>
                                        </Form.Item>
                                        <span> ^ </span>
                                        <Form.Item
                                            {...useForm.validateInfos[`${prefix}microAnalysisResult3`]}
                                            help={undefined}
                                            class="d-inline-block"
                                        >
                                            <InputNumber {...inputPropField3}></InputNumber>
                                        </Form.Item>
                                        <span>&nbsp;&nbsp;&nbsp;</span>
                                        <Form.Item {...useForm.validateInfos[`${prefix}refUnit`]} help={undefined} class="d-inline-block">
                                            <CSelect {...inputPropField4}></CSelect>
                                        </Form.Item>
                                    </>
                                );
                            }
                        },
                        {
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}microAnalysisQty1`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({
                                labelCol: { xs: 24, md: 8 },
                                wrapperCol: { xs: 24, md: 16 },
                                validateStatus: undefined,
                                help: undefined
                            }),
                            label: () => i18n.global.t("views.request.hazInfo.microAnalysisQty1"),
                            inputRender: () => {
                                const inputPropField1 = lodash.merge(
                                    {
                                        value: model.microAnalysisQty1,
                                        "onUpdate:value": value => (model.microAnalysisQty1 = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}microAnalysisQty1` }, model }
                                    })
                                );
                                const inputPropField2 = lodash.merge(
                                    {
                                        value: model.microAnalysisQty2,
                                        "onUpdate:value": value => (model.microAnalysisQty2 = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}microAnalysisQty2` }, model }
                                    })
                                );
                                const inputPropField3 = lodash.merge(
                                    {
                                        value: model.microAnalysisQty3,
                                        "onUpdate:value": value => (model.microAnalysisQty3 = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}microAnalysisQty3` }, model }
                                    })
                                );
                                const inputPropField4 = lodash.merge(
                                    {
                                        value: model.refMicroUnit,
                                        "onUpdate:value": value => (model.refMicroUnit = value),
                                        disabled: viewOnly,
                                        list: callHazSubStanceUnit,
                                        optLabel: "value",
                                        placeholder: i18n.global.t("button.unit"),
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}refMicroUnit` }, model }
                                    })
                                );
                                return (
                                    <>
                                        <Form.Item {...useForm.validateInfos[`${prefix}microAnalysisQty1`]} help={undefined} class="d-inline-block">
                                            <InputNumber {...inputPropField1}></InputNumber>
                                        </Form.Item>
                                        <span> x </span>
                                        <Form.Item {...useForm.validateInfos[`${prefix}microAnalysisQty2`]} help={undefined} class="d-inline-block">
                                            <InputNumber {...inputPropField2}></InputNumber>
                                        </Form.Item>
                                        <span> ^ </span>
                                        <Form.Item {...useForm.validateInfos[`${prefix}microAnalysisQty3`]} help={undefined} class="d-inline-block">
                                            <InputNumber {...inputPropField3}></InputNumber>
                                        </Form.Item>
                                        <span>&nbsp;&nbsp;&nbsp;</span>
                                        <Form.Item {...useForm.validateInfos[`${prefix}refMicroUnit`]} help={undefined} class="d-inline-block">
                                            <CSelect {...inputPropField4}></CSelect>
                                        </Form.Item>
                                    </>
                                );
                            }
                        },
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}refSubStance.casNo`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazSubStance.casNo"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refSubStance?.casNo,
                                        "onUpdate:value": value => (model.refSubStance.casNo = value),
                                        disabled: true
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                )
                        }),
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}refMainSubStanceRate`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("views.request.hazInfo.refMainSubStanceRate"),
                            inputProp: ({ field }) => {
                                // type must change from refMainSubStance.isSolid = '1' or refMainSubStance.isLiquid = '2'
                                const type = modelRef?.refHazTemp?.isLiquid === true ? "2" : "1";
                                const rate = calSubStanceRate({ type: type, qty: model.qty });
                                return lodash.merge(
                                    {
                                        value: rate ? `${rate.min} - ${rate.max}` : "",
                                        "onUpdate:value": value => (model.refMainSubStanceRate = value),
                                        disabled: true
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                );
                            }
                        })
                    ],
                    skeleton: isLoading.value
                })
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <div></div>
            },
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldCheckbox({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}isImport`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 }, colon: false }),
                            label: () => i18n.global.t("model.hazMainSubStance.isImport"),
                            labelRender: () => " ",
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        checked: model.isImport,
                                        "onUpdate:checked": checked => {
                                            model.isImport = checked;
                                            lodash.remove(model.companies, d => !d.id);
                                            model.companies.forEach(d => (d.isDelete = true));
                                            lodash.remove(model.products, d => !d.id);
                                            model.products.forEach(d => (d.isDelete = true));
                                        },
                                        disabled: viewOnly,
                                        text: () => i18n.global.t("model.hazMainSubStance.isImport")
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                )
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}companies`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("views.company.name"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.companies,
                                        "onUpdate:value": value => (model.companies = value),
                                        onDeselect: value => {
                                            lodash.remove(model.products, d => !d.id && d.refHaz.refUserCompany.id === value.refUserCompany?.id);
                                            model.products
                                                .filter(d => d.refHaz.refUserCompany.id === value.refUserCompany?.id)
                                                .forEach(d => (d.isDelete = true));
                                        },
                                        disabled: viewOnly || !model.isImport,
                                        list: ({ searchQuery }) =>
                                            callUserCompany({ searchQuery }).then(items => items.map(item => ({ refUserCompany: item }))),
                                        renderLabel: ({ item }) => {
                                            if (item.refUserCompany.refPersonType?.key === "1") {
                                                return <div>{item.refUserCompany.companyNameTh}</div>;
                                            }
                                            return <div>{item.refUserCompany.fullNameTh}</div>;
                                        },
                                        mode: "multiple",
                                        optKey: "refUserCompany.id"
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                ),
                            changeLog: {
                                getValueChildren: ({ record }) =>
                                    record.refUserCompany.refPersonType?.key === "1"
                                        ? record.refUserCompany?.companyNameTh
                                        : record.refUserCompany?.fullNameTh
                            }
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}products`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("views.request.hazInfo.hazId"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.products,
                                        "onUpdate:value": value => (model.products = value),
                                        disabled: viewOnly || !model.isImport,
                                        list: ({ searchQuery }) => {
                                            return filter({
                                                list: model.companies.filter(d => d.isDelete !== true).flatMap(d => d.refUserCompany.hazes),
                                                where: [{ name: "approveCode", type: "String", operator: "like", value: searchQuery }],
                                                sort: [{ name: "approveCode", type: "DESC" }]
                                            }).map(item => ({ refHaz: item }));
                                        },
                                        optLabel: "refHaz.approveCode",
                                        mode: "multiple",
                                        optKey: "refHaz.id"
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                ),
                            changeLog: { getValueChildren: ({ record }) => record.refHaz?.approveCode }
                        })
                    ],
                    skeleton: isLoading.value
                })
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldCheckbox({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}isInternal`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 }, colon: false }),
                            label: () => i18n.global.t("model.hazMainSubStance.isInternal"),
                            labelRender: () => " ",
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        checked: model.isInternal,
                                        "onUpdate:checked": checked => (model.isInternal = checked),
                                        disabled: viewOnly,
                                        text: () => i18n.global.t("model.hazMainSubStance.isInternal")
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                )
                        }),
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}docNoRefer`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazMainSubStance.docNoRefer"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.docNoRefer,
                                        "onUpdate:value": value => (model.docNoRefer = value),
                                        disabled: viewOnly || !model.isInternal
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                )
                        }),
                        createFieldAttachment({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}attachments.0`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.attachment.modelName"),
                            inputProp: ({ field }) => {
                                return lodash.merge(
                                    {
                                        value: model.attachments?.[0]?.url || model.attachments?.[0]?.blob ? model.attachments?.[0] : null,
                                        "onUpdate:value": value => {
                                            const refAttach = model.attachments?.[0];
                                            refAttach.name = value?.name;
                                            refAttach.documentName = refAttach.documentName ? refAttach.documentName : value?.name;
                                            refAttach.blob = value?.blob;
                                            refAttach.isSave = value?.isSave;
                                            refAttach.size = value?.size;
                                            refAttach.type = value?.type;
                                            refAttach.group = value?.group;
                                        },
                                        disabled: viewOnly || !model.isInternal
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                );
                            }
                        })
                    ],
                    skeleton: isLoading.value
                })
            })
        ]
    });
};

export const initData = ({ props, data }) => {
    const { /* menuContext, viewOnly,  */ itemsKey /* , canCreate, canDelete */ } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    /*
    data.columns = createColumns({ props, data });
    data.page = createPage({ data: items });
    data.tableOptions = { search: { activeKey: null } };
    data.manages = [
        createManageDelete({
            menuContext,
            visible: () => (viewOnly ? false : canDelete),
            onClick: ({ rowSelection }) => {
                onClickDeleteAllClient({ rowSelection, useForm, items, key: itemsKey });
                data.page.page = 0;
            }
        }),
        createManageExportCsv({ menuContext })
    ];
    data.manageExtras = [
        {
            render: () => createButtonCreate({ onClick: () => items.push({ companies: [], products: [] }) }),
            visible: () => (viewOnly ? false : canCreate)
        }
    ];
    data.rowActions = [];
    */

    fetchData({ props, data }).then(page => items.push(...page.data));
};

export const fetchData = ({ props }) => {
    const { fetchCriteria } = props;
    if (!fetchCriteria || fetchCriteria.fetched) {
        return Promise.resolve({ data: [] });
    }
    fetchCriteria.fetched = true;
    setIsLoading(true);
    return callSearch({ criteria: fetchCriteria }).finally(() => setIsLoading(false));
};

export const calSubStanceRate = ({ type, qty }) => {
    if (!type || (qty?.toString?.()?.length || 0) === 0) return null;
    qty = parseFloat(qty);
    const factor = getFactorSubStanceRate({ type, qty });
    if (qty <= 2.5) {
        const rate = qty * factor;
        return { min: qty - rate, max: qty + rate };
    } else if (qty <= 50) {
        const rate = qty * factor;
        return { min: qty - rate, max: qty + rate };
    } else {
        return { min: qty - factor, max: qty + factor };
    }
};

export const getFactorSubStanceRate = ({ type, qty }) => {
    // 1 solid, 2 liquid
    if (qty <= 2.5) {
        return type === "2" ? 0.15 : 0.25;
    } else if (qty <= 10) {
        return 0.1;
    } else if (qty <= 25) {
        return 0.06;
    } else if (qty <= 50) {
        return 0.05;
    } else {
        return 2.5;
    }
};
